import React, { forwardRef } from "react";
import classnames from "classnames";

import styles from "./AntdLite.module.scss";

// Drop-in replacements for the antd primitives used in always-rendered layout
// chrome (Navbar, Footer, hero Slide, Features). Markup and base CSS mirror
// antd v5's rendered output (see AntdLite.module.scss) so visuals are
// unchanged, without pulling antd into the initial bundle. Heavier antd
// components (Drawer, Input, forms) stay on antd, loaded in deferred chunks.

type FlexProps = React.HTMLAttributes<HTMLDivElement> & {
    vertical?: boolean;
    gap?: number | string;
    justify?: React.CSSProperties["justifyContent"];
    align?: React.CSSProperties["alignItems"];
    wrap?: boolean | React.CSSProperties["flexWrap"];
};

export const Flex = forwardRef<HTMLDivElement, FlexProps>(function Flex(props, ref) {
    const { vertical, gap, justify, align, wrap, className, style, children, ...rest } = props;

    return (
        <div
            ref={ref}
            className={classnames(styles.flex, { [styles.flexVertical]: vertical }, className)}
            style={{
                ...(gap !== undefined ? { gap } : null),
                ...(justify ? { justifyContent: justify } : null),
                ...(align ? { alignItems: align } : null),
                ...(wrap ? { flexWrap: wrap === true ? "wrap" : wrap } : null),
                ...style,
            }}
            {...rest}
        >
            {children}
        </div>
    );
});

type DividerProps = React.HTMLAttributes<HTMLDivElement>;

export const Divider: React.FC<DividerProps> = ({ className, ...rest }) => (
    <div role="separator" className={classnames(styles.divider, className)} {...rest} />
);

type TextProps = React.HTMLAttributes<HTMLSpanElement>;

export const Text: React.FC<TextProps> = ({ className, children, ...rest }) => (
    <span className={classnames(styles.text, className)} {...rest}>
        {children}
    </span>
);

type TitleProps = React.HTMLAttributes<HTMLHeadingElement> & {
    level?: 1 | 2 | 3 | 4 | 5;
};

export const Title: React.FC<TitleProps> = ({ level = 1, className, children, ...rest }) => {
    const Tag = `h${level}` as keyof React.JSX.IntrinsicElements;

    return React.createElement(
        Tag,
        // Literal "ant-typography" kept so globals.css h*.ant-typography rules
        // still apply, as they did to antd's Title.
        { className: classnames("ant-typography", styles.title, className), ...rest },
        children
    );
};

type IconProps = {
    className?: string;
    onClick?: React.MouseEventHandler<HTMLSpanElement>;
};

const createIcon = (label: string, paths: string[], fillRule?: "evenodd") => {
    const Icon: React.FC<IconProps> = ({ className, onClick }) => (
        <span
            role="img"
            aria-label={label}
            className={classnames(styles.anticon, className)}
            onClick={onClick}
        >
            <svg
                viewBox="64 64 896 896"
                focusable="false"
                width="1em"
                height="1em"
                fill="currentColor"
                aria-hidden="true"
                {...(fillRule ? { fillRule } : null)}
            >
                {paths.map((d, i) => (
                    <path key={i} d={d} />
                ))}
            </svg>
        </span>
    );

    Icon.displayName = `${label}Icon`;

    return Icon;
};

// Path data copied verbatim from @ant-design/icons-svg — renders identically.
export const MenuOutlined = createIcon("menu", [
    "M904 160H120c-4.4 0-8 3.6-8 8v64c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-64c0-4.4-3.6-8-8-8zm0 624H120c-4.4 0-8 3.6-8 8v64c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-64c0-4.4-3.6-8-8-8zm0-312H120c-4.4 0-8 3.6-8 8v64c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-64c0-4.4-3.6-8-8-8z",
]);

export const CloseOutlined = createIcon(
    "close",
    [
        "M799.86 166.31c.02 0 .04.02.08.06l57.69 57.7c.04.03.05.05.06.08a.12.12 0 010 .06c0 .03-.02.05-.06.09L569.93 512l287.7 287.7c.04.04.05.06.06.09a.12.12 0 010 .07c0 .02-.02.04-.06.08l-57.7 57.69c-.03.04-.05.05-.07.06a.12.12 0 01-.07 0c-.03 0-.05-.02-.09-.06L512 569.93l-287.7 287.7c-.04.04-.06.05-.09.06a.12.12 0 01-.07 0c-.02 0-.04-.02-.08-.06l-57.69-57.7c-.04-.03-.05-.05-.06-.07a.12.12 0 010-.07c0-.03.02-.05.06-.09L454.07 512l-287.7-287.7c-.04-.04-.05-.06-.06-.09a.12.12 0 010-.07c0-.02.02-.04.06-.08l57.7-57.69c.03-.04.05-.05.07-.06a.12.12 0 01.07 0c.03 0 .05.02.09.06L512 454.07l287.7-287.7c.04-.04.06-.05.09-.06a.12.12 0 01.07 0z",
    ],
    "evenodd"
);

export const InstagramOutlined = createIcon("instagram", [
    "M512 306.9c-113.5 0-205.1 91.6-205.1 205.1S398.5 717.1 512 717.1 717.1 625.5 717.1 512 625.5 306.9 512 306.9zm0 338.4c-73.4 0-133.3-59.9-133.3-133.3S438.6 378.7 512 378.7 645.3 438.6 645.3 512 585.4 645.3 512 645.3zm213.5-394.6c-26.5 0-47.9 21.4-47.9 47.9s21.4 47.9 47.9 47.9 47.9-21.3 47.9-47.9a47.84 47.84 0 00-47.9-47.9zM911.8 512c0-55.2.5-109.9-2.6-165-3.1-64-17.7-120.8-64.5-167.6-46.9-46.9-103.6-61.4-167.6-64.5-55.2-3.1-109.9-2.6-165-2.6-55.2 0-109.9-.5-165 2.6-64 3.1-120.8 17.7-167.6 64.5C132.6 226.3 118.1 283 115 347c-3.1 55.2-2.6 109.9-2.6 165s-.5 109.9 2.6 165c3.1 64 17.7 120.8 64.5 167.6 46.9 46.9 103.6 61.4 167.6 64.5 55.2 3.1 109.9 2.6 165 2.6 55.2 0 109.9.5 165-2.6 64-3.1 120.8-17.7 167.6-64.5 46.9-46.9 61.4-103.6 64.5-167.6 3.2-55.1 2.6-109.8 2.6-165zm-88 235.8c-7.3 18.2-16.1 31.8-30.2 45.8-14.1 14.1-27.6 22.9-45.8 30.2C695.2 844.7 570.3 840 512 840c-58.3 0-183.3 4.7-235.9-16.1-18.2-7.3-31.8-16.1-45.8-30.2-14.1-14.1-22.9-27.6-30.2-45.8C179.3 695.2 184 570.3 184 512c0-58.3-4.7-183.3 16.1-235.9 7.3-18.2 16.1-31.8 30.2-45.8s27.6-22.9 45.8-30.2C328.7 179.3 453.7 184 512 184s183.3-4.7 235.9 16.1c18.2 7.3 31.8 16.1 45.8 30.2 14.1 14.1 22.9 27.6 30.2 45.8C844.7 328.7 840 453.7 840 512c0 58.3 4.7 183.2-16.2 235.8z",
]);

export const YoutubeFilled = createIcon("youtube", [
    "M941.3 296.1a112.3 112.3 0 00-79.2-79.3C792.2 198 512 198 512 198s-280.2 0-350.1 18.7A112.12 112.12 0 0082.7 296C64 366 64 512 64 512s0 146 18.7 215.9c10.3 38.6 40.7 69 79.2 79.3C231.8 826 512 826 512 826s280.2 0 350.1-18.8c38.6-10.3 68.9-40.7 79.2-79.3C960 658 960 512 960 512s0-146-18.7-215.9zM423 646V378l232 133-232 135z",
]);

export const TikTokFilled = createIcon(
    "tik-tok",
    [
        "M912 224.96C912 162.57 861.42 112 799.04 112H224.96C162.57 112 112 162.57 112 224.96v574.08C112 861.43 162.58 912 224.96 912h574.08C861.42 912 912 861.43 912 799.04zM774.76 460.92c-51.62.57-99.71-15.03-141.94-43.93v202.87a192.3 192.3 0 01-149 187.85c-119.06 27.17-219.86-58.95-232.57-161.83-13.3-102.89 52.32-193.06 152.89-213.29 19.65-4.04 49.2-4.04 64.46-.57v108.66c-4.7-1.15-9.09-2.31-13.71-2.89-39.3-6.94-77.37 12.72-92.98 48.55-15.6 35.84-5.16 77.45 26.63 101.73 26.59 20.8 56.09 23.7 86.14 9.82 30.06-13.29 46.21-37.56 49.68-70.5.58-4.63.54-9.84.54-15.04V222.21c0-10.99.09-10.5 11.07-10.5h86.12c6.36 0 8.67.9 9.25 8.43 4.62 67.04 55.53 124.14 120.84 132.81 6.94 1.16 14.37 1.62 22.58 2.2z",
    ],
    "evenodd"
);
